/**
 * Reads the login address a product sent to Auth0 (`ext-wb_host`) and looks
 * up the reseller branding admin keeps for it
 * (`GET /api/public/login-branding`).
 *
 * Everything here fails closed: a missing or malformed address, an
 * unconfigured admin URL, a network error, a timeout or an unexpected payload
 * all come back as `null`, and the login shell renders exactly as it does
 * without a reseller.
 */

import type { UntrustedDataMembers } from "@auth0/auth0-acul-react/types";

import { getAdminApiBaseUrl } from "@/utils/api/requestPasswordReset";

export interface ResellerBranding {
  displayName: string;
  logoUrl?: string;
  isotypeUrl?: string;
  colorPrimary?: string;
  colorAccent?: string;
}

export type AuthorizationParams = UntrustedDataMembers["authorizationParams"];

export const LOGIN_HOST_PARAM = "ext-wb_host";

const LOOKUP_TIMEOUT_MS = 2500;
const MAX_HOST_LENGTH = 253;
const MAX_DISPLAY_NAME_LENGTH = 80;
const HOST_LABEL = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;
const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;

/**
 * The login address, normalised by the same rules as admin's
 * `normalizeLoginHost`: trim, lowercase, drop one trailing dot, then require
 * at most 253 characters and at least two DNS labels. The label rule also
 * rules out a scheme, port, path or wildcard.
 */
export function readLoginHost(
  params: AuthorizationParams | null | undefined
): string | null {
  const raw = params?.[LOGIN_HOST_PARAM];
  if (typeof raw !== "string") return null;

  const host = raw.trim().toLowerCase().replace(/\.$/, "");
  if (host.length === 0 || host.length > MAX_HOST_LENGTH) return null;

  const labels = host.split(".");
  if (labels.length < 2) return null;
  return labels.every((label) => HOST_LABEL.test(label)) ? host : null;
}

export function isHexColor(value: unknown): value is string {
  return typeof value === "string" && HEX_COLOR.test(value);
}

function httpsUrl(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  try {
    return new URL(value).protocol === "https:" ? value : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Validates a lookup payload. A display name of 1–80 characters (after
 * trimming) is required; any other field that is not an https URL or a
 * `#rrggbb` colour is dropped.
 */
export function parseResellerBranding(json: unknown): ResellerBranding | null {
  if (typeof json !== "object" || json === null || Array.isArray(json)) {
    return null;
  }
  const data = json as Record<string, unknown>;

  if (typeof data.displayName !== "string") return null;
  const displayName = data.displayName.trim();
  if (
    displayName.length === 0 ||
    displayName.length > MAX_DISPLAY_NAME_LENGTH
  ) {
    return null;
  }

  const branding: ResellerBranding = { displayName };
  const logoUrl = httpsUrl(data.logoUrl);
  const isotypeUrl = httpsUrl(data.isotypeUrl);
  if (logoUrl) branding.logoUrl = logoUrl;
  if (isotypeUrl) branding.isotypeUrl = isotypeUrl;
  if (isHexColor(data.colorPrimary)) branding.colorPrimary = data.colorPrimary;
  if (isHexColor(data.colorAccent)) branding.colorAccent = data.colorAccent;
  return branding;
}

interface FetchResellerBrandingOptions {
  fetchImpl?: typeof fetch;
  timeoutMs?: number;
}

// One lookup per host and client for the life of the page.
const lookups = new Map<string, Promise<ResellerBranding | null>>();

/**
 * Looks up the branding for a login address and client. Memoised per
 * `host|clientId`, never throws, and resolves `null` after `timeoutMs`
 * (2.5 s by default) even if the request never settles.
 */
export function fetchResellerBranding(
  host: string,
  clientId: string,
  opts: FetchResellerBrandingOptions = {}
): Promise<ResellerBranding | null> {
  const key = `${host}|${clientId}`;
  let lookup = lookups.get(key);
  if (!lookup) {
    lookup = lookUp(host, clientId, opts);
    lookups.set(key, lookup);
  }
  return lookup;
}

async function lookUp(
  host: string,
  clientId: string,
  { fetchImpl, timeoutMs = LOOKUP_TIMEOUT_MS }: FetchResellerBrandingOptions
): Promise<ResellerBranding | null> {
  const base = getAdminApiBaseUrl();
  if (!base) return null;

  const url =
    `${base}/api/public/login-branding` +
    `?host=${encodeURIComponent(host)}&clientId=${encodeURIComponent(clientId)}`;
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;

  const request = (async (): Promise<ResellerBranding | null> => {
    const res = await (fetchImpl ?? fetch)(url, {
      method: "GET",
      mode: "cors",
      credentials: "omit",
      signal: controller.signal,
    });
    if (res.status !== 200) return null;
    return parseResellerBranding(await res.json());
  })();
  const timeout = new Promise<null>((resolve) => {
    timer = setTimeout(() => {
      controller.abort();
      resolve(null);
    }, timeoutMs);
  });

  try {
    return await Promise.race([request, timeout]);
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

/** Forgets every lookup. For tests, which share one module instance. */
export function clearResellerBrandingCache(): void {
  lookups.clear();
}
